/**
 * Standardized time period definitions for financial calculations
 * Used to ensure consistent time period handling across all financial services
 */

/**
 * Enumeration of supported time periods for financial calculations
 */
export enum TimePeriod {
  DAILY = 'daily',
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
  YEARLY = 'yearly'
}

/**
 * Interface representing a date range with a specific time period type
 */
export interface DatePeriod {
  /** Start date of the period (inclusive) */
  startDate: Date;
  
  /** End date of the period (inclusive) */
  endDate: Date;
  
  /** Type of time period */
  type: TimePeriod;
  
  /** Optional label for the period (e.g., "Q1 2024", "January 2024") */
  label?: string;
}

/**
 * Interface for a period-specific label formatter
 */
export interface PeriodLabelFormatter {
  /**
   * Formats a date period into a human-readable label
   * @param period The date period to format
   * @returns A formatted label string
   */
  formatPeriodLabel(period: DatePeriod): string;
}

/**
 * Default formatters for different period types
 */
export const DEFAULT_PERIOD_FORMATTERS: Record<TimePeriod, PeriodLabelFormatter> = {
  [TimePeriod.DAILY]: {
    formatPeriodLabel: (period: DatePeriod) => 
      period.startDate.toLocaleDateString('fr-BE')
  },
  [TimePeriod.MONTHLY]: {
    formatPeriodLabel: (period: DatePeriod) => 
      period.startDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
  },
  [TimePeriod.QUARTERLY]: {
    formatPeriodLabel: (period: DatePeriod) => {
      const quarter = Math.floor(period.startDate.getMonth() / 3) + 1;
      const year = period.startDate.getFullYear();
      return `Q${quarter} ${year}`;
    }
  },
  [TimePeriod.YEARLY]: {
    formatPeriodLabel: (period: DatePeriod) => 
      period.startDate.getFullYear().toString()
  }
}; 